/**
 * Channel management.
 *
 * One screen for the whole hierarchy: Tictac → distributors → resellers → end
 * customers. Deliberately a tree rather than a flat table, because in a channel
 * business the useful question is almost never "list every organisation" — it is
 * "who sits under this distributor, what are they paying for, and how much
 * credit have they got left". A table answers the first question; only
 * indentation answers the second.
 *
 * Every rule the tier selector and the buttons here express is also enforced by
 * the server. Hiding a choice the user cannot make is politeness; it is never
 * what stops them. See api/src/modules/organizations/service.ts.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, qs } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useApiError } from '../lib/useApiError';
import {
  Card, Button, Pill, Modal, Field, EmptyState, Skeleton,
  ErrorNotice, StatTile, type Tone,
} from '../components/ui';
import { IconShield, IconCredits } from '../components/icons';
import { TIERS, PLANS, LANGUAGES, LANGUAGE_NAMES, type Tier, type Plan, type Language } from '@opencomply/shared';
import { cn } from '../lib/cn';

interface ChannelOrganization {
  id: string;
  name: string;
  description: string | null;
  domain: string | null;
  email: string | null;
  phone: string | null;
  tier: string;
  plan: string;
  planExpiresAt: string | null;
  policyLanguage: string;
  parentId: string | null;
  managementConsent: boolean;
  isActive: boolean;
  createdAt: string;
  /** Distance from the acting organisation; 0 is the actor itself. */
  depth: number;
  isSelf: boolean;
  creditBalance: number;
  userCount: number;
}

/**
 * Tier → tone. A tier is not a risk level, so this is not the status ramp: it
 * reads as position in the channel, brand-first at the top and recessive at the
 * leaves, which is the only thing the colour is being asked to say.
 */
const TIER_TONE: Record<string, Tone> = {
  SUPERADMIN: 'serious',
  DISTRIBUTOR: 'good',
  PARTNER: 'warning',
  CLIENT: 'neutral',
  VENDOR: 'neutral',
};

/** Days of remaining licence below which the expiry is called out. */
const EXPIRY_WARNING_DAYS = 30;

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

export function OrganizationsPage() {
  const { t, i18n } = useTranslation();
  const { allows, session } = useAuth();
  const queryClient = useQueryClient();
  const describeError = useApiError();

  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<ChannelOrganization | null>(null);
  const [consenting, setConsenting] = useState<ChannelOrganization | null>(null);
  const [deactivating, setDeactivating] = useState<ChannelOrganization | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading, error: listError, refetch } = useQuery({
    queryKey: ['organizations', { search, tierFilter }],
    queryFn: () =>
      api.get<{ items: ChannelOrganization[]; total: number; truncated: boolean }>(
        `/organizations${qs({ search, tier: tierFilter })}`,
      ),
  });

  /**
   * The tiers the server will accept. Fetched rather than derived locally: the
   * rule lives in one place on the server, and a selector built from a
   * duplicated copy of it would eventually offer a choice that 400s.
   */
  const { data: creatable } = useQuery({
    queryKey: ['organizations', 'creatable-tiers'],
    queryFn: () => api.get<{ tiers: Tier[] }>('/organizations/creatable-tiers'),
  });

  const items = data?.items ?? [];
  const creatableTiers = creatable?.tiers ?? [];

  const formatDate = (iso: string | null) =>
    iso ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(iso)) : '—';

  /**
   * Whether this session is an external one — an upstream manager present by
   * virtue of the host organisation's consent. Such a session may not touch the
   * consent flag at all (the server refuses it), so the toggle is hidden rather
   * than offered and then rejected.
   */
  const actingExternally =
    session?.organizations.find((o) => o.id === session.organization.id)?.isExternal ?? false;

  const expiringSoon = items.filter((o) => {
    const days = daysUntil(o.planExpiresAt);
    return days !== null && days >= 0 && days <= EXPIRY_WARNING_DAYS;
  }).length;

  const kpis = {
    // The actor's own organisation is the root of the tree, not a customer, so
    // the channel count excludes it — otherwise a brand-new distributor reads
    // "1 organisation" when it has sold to nobody.
    channel: items.filter((o) => !o.isSelf).length,
    inactive: items.filter((o) => !o.isActive).length,
    expiringSoon,
    consenting: items.filter((o) => o.managementConsent && !o.isSelf).length,
  };

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['organizations'] });

  return (
    <div className="oc-page space-y-5 animate-fade-in">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="oc-h1">{t('organizations.title')}</h1>
          <p className="oc-muted mt-1">{t('organizations.subtitle')}</p>
        </div>
        {allows('org.create') && creatableTiers.length > 0 && (
          <Button size="sm" variant="primary" onClick={() => { setError(null); setAddOpen(true); }}>
            {t('organizations.add')}
          </Button>
        )}
      </header>

      {error && <ErrorNotice message={error} />}

      {items.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <StatTile label={t('organizations.inChannel')} value={kpis.channel} />
          <StatTile label={t('organizations.expiringSoon')} value={kpis.expiringSoon}
                    tone={kpis.expiringSoon > 0 ? 'warning' : undefined} />
          <StatTile label={t('organizations.deactivated')} value={kpis.inactive}
                    tone={kpis.inactive > 0 ? 'serious' : undefined} />
          <StatTile label={t('organizations.consenting')} value={kpis.consenting} />
        </div>
      )}

      <Card className="!p-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            className="oc-input sm:max-w-xs" type="search"
            placeholder={t('organizations.searchPlaceholder')}
            value={search} onChange={(e) => setSearch(e.target.value)}
          />
          <select className="oc-input sm:!w-auto" value={tierFilter}
                  onChange={(e) => setTierFilter(e.target.value)}
                  aria-label={t('organizations.tier')}>
            <option value="">{t('common.all')}</option>
            {TIERS.map((tier) => (
              <option key={tier} value={tier}>{t(`tier.${tier}`)}</option>
            ))}
          </select>
        </div>
      </Card>

      {data?.truncated && <ErrorNotice message={t('organizations.truncated')} />}

      {isLoading ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24" />)}</div>
      ) : listError ? (
        <ErrorNotice message={describeError(listError)} onRetry={() => void refetch()}
                     retryLabel={t('common.retry')} />
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            icon={<IconShield className="w-10 h-10" />}
            title={t('organizations.none')}
            description={t('organizations.noneHint')}
            action={allows('org.create') && creatableTiers.length > 0
              ? <Button variant="primary" onClick={() => setAddOpen(true)}>{t('organizations.add')}</Button>
              : undefined}
          />
        </Card>
      ) : (
        <ul className="space-y-2">
          {items.map((org) => (
            <OrganizationRow
              key={org.id}
              org={org}
              formatDate={formatDate}
              canEdit={allows('org.update')}
              // Consent is the organisation's own decision to give, so granting
              // is only ever offered on your own row. Withdrawing is offered
              // from above too, because it can only ever remove access.
              canGrantConsent={allows('org.consent.manage') && org.isSelf && !actingExternally}
              canWithdrawConsent={allows('org.consent.manage') && !actingExternally}
              canDeactivate={allows('org.delete') && !org.isSelf}
              onEdit={() => { setError(null); setEditing(org); }}
              onConsent={() => { setError(null); setConsenting(org); }}
              onToggleActive={() => { setError(null); setDeactivating(org); }}
            />
          ))}
        </ul>
      )}

      <AddOrganizationModal
        open={addOpen}
        creatableTiers={creatableTiers}
        parentName={session?.organization.name ?? ''}
        onClose={() => setAddOpen(false)}
        onSaved={() => { setAddOpen(false); invalidate(); }}
      />
      <EditOrganizationModal
        org={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); invalidate(); }}
      />
      <ConsentModal
        org={consenting}
        onClose={() => setConsenting(null)}
        onSaved={() => {
          setConsenting(null);
          invalidate();
          // Consent governs which organisations the session may switch into, so
          // the session's own organisation list is now stale.
          void queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
        }}
      />
      <DeactivateModal
        org={deactivating}
        onClose={() => setDeactivating(null)}
        onSaved={() => { setDeactivating(null); invalidate(); }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * One organisation in the tree.
 *
 * INDENTATION IS CAPPED AT FOUR LEVELS. On a 360px screen an uncapped indent
 * turns the fifth generation into a column two words wide. Past the cap the
 * depth is stated as a number instead, which stays readable and still says
 * "this is deeper than its neighbour".
 */
function OrganizationRow({
  org, formatDate, canEdit, canGrantConsent, canWithdrawConsent, canDeactivate,
  onEdit, onConsent, onToggleActive,
}: {
  org: ChannelOrganization;
  formatDate: (iso: string | null) => string;
  canEdit: boolean;
  canGrantConsent: boolean;
  canWithdrawConsent: boolean;
  canDeactivate: boolean;
  onEdit: () => void;
  onConsent: () => void;
  onToggleActive: () => void;
}) {
  const { t } = useTranslation();

  const MAX_INDENT_LEVELS = 4;
  const indentLevels = Math.min(org.depth, MAX_INDENT_LEVELS);
  const days = daysUntil(org.planExpiresAt);
  const expired = days !== null && days < 0;
  const expiringSoon = days !== null && days >= 0 && days <= EXPIRY_WARNING_DAYS;

  const showConsentToggle = org.managementConsent ? canWithdrawConsent : canGrantConsent;

  return (
    <li style={{ marginInlineStart: `${indentLevels * 0.875}rem` }}>
      <div className={cn(
        'oc-card p-3 sm:p-4',
        // A left rule stands in for the tree's connector lines: cheap, and it
        // survives the indent cap, which drawn connectors would not.
        org.depth > 0 && 'border-l-[3px] border-l-brand/30',
        !org.isActive && 'opacity-60',
      )}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-medium text-ink truncate">{org.name}</p>
            {org.domain && (
              <p className="text-xs text-ink-muted font-mono truncate">{org.domain}</p>
            )}
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <Pill tone={TIER_TONE[org.tier] ?? 'neutral'}>
              {t(`tier.${org.tier}`, { defaultValue: org.tier })}
            </Pill>
            {org.depth > MAX_INDENT_LEVELS && (
              <span className="text-[10px] text-ink-muted tabular-nums">
                {t('organizations.depth', { count: org.depth })}
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 mt-2">
          {org.isSelf && <Pill tone="neutral">{t('organizations.you')}</Pill>}
          <Pill tone={org.plan === 'FULL' ? 'good' : 'neutral'}>
            {t(`organizations.plans.${org.plan}`, { defaultValue: org.plan })}
          </Pill>
          {org.planExpiresAt && (
            <Pill tone={expired ? 'critical' : expiringSoon ? 'warning' : 'neutral'}>
              {expired
                ? t('organizations.expired', { date: formatDate(org.planExpiresAt) })
                : t('organizations.expires', { date: formatDate(org.planExpiresAt) })}
            </Pill>
          )}
          {!org.isActive && <Pill tone="serious">{t('organizations.inactive')}</Pill>}
          {/* Consent state is shown on every row, granted or not: "who may read
              this customer's compliance data" is not a detail to go hunting for. */}
          <Pill tone={org.managementConsent ? 'good' : 'neutral'}>
            {org.managementConsent
              ? t('organizations.consentGranted')
              : t('organizations.consentWithheld')}
          </Pill>
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2.5 text-xs text-ink-secondary">
          <span className="inline-flex items-center gap-1.5">
            <IconCredits className="w-3.5 h-3.5 text-ink-muted" />
            <span className="tabular-nums font-medium text-ink">{org.creditBalance}</span>
            {t('organizations.credits')}
          </span>
          <span>
            <span className="tabular-nums font-medium text-ink">{org.userCount}</span>{' '}
            {t('organizations.users', { count: org.userCount })}
          </span>
          {org.policyLanguage && (
            <span className="uppercase tracking-wide text-ink-muted">{org.policyLanguage}</span>
          )}
        </div>

        {(canEdit || showConsentToggle || canDeactivate) && (
          <div className="flex flex-wrap gap-2 mt-3">
            {canEdit && (
              <Button size="sm" onClick={onEdit}>{t('common.edit')}</Button>
            )}
            {showConsentToggle && (
              <Button size="sm" variant={org.managementConsent ? 'danger' : 'secondary'}
                      onClick={onConsent}>
                {org.managementConsent
                  ? t('organizations.withdrawConsent')
                  : t('organizations.grantConsent')}
              </Button>
            )}
            {canDeactivate && (
              <Button size="sm" variant={org.isActive ? 'danger' : 'secondary'}
                      onClick={onToggleActive}>
                {org.isActive ? t('organizations.deactivate') : t('organizations.reactivate')}
              </Button>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------

/**
 * Create an organisation.
 *
 * The parent is stated, never chosen: the server always parents a new
 * organisation to the actor's own, so offering a picker would be a lie about
 * what the form does. The tier selector lists only what the server will accept.
 */
function AddOrganizationModal({ open, creatableTiers, parentName, onClose, onSaved }: {
  open: boolean;
  creatableTiers: Tier[];
  parentName: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const describeError = useApiError();

  const [form, setForm] = useState({
    name: '', tier: '', domain: '', email: '', phone: '', description: '',
    plan: 'TRIAL' as Plan, planExpiresAt: '', policyLanguage: 'en' as Language,
  });
  const [error, setError] = useState<string | null>(null);
  const [suggestedRoot, setSuggestedRoot] = useState<string | null>(null);

  // Default to the lowest tier offered — the common case by a wide margin is a
  // reseller adding an end customer, not a distributor adding a distributor.
  const tier = form.tier || creatableTiers[creatableTiers.length - 1] || '';

  const create = useMutation({
    mutationFn: () =>
      api.post('/organizations', {
        name: form.name,
        tier,
        domain: form.domain || null,
        email: form.email || null,
        phone: form.phone || null,
        description: form.description || null,
        plan: form.plan,
        planExpiresAt: form.planExpiresAt || null,
        policyLanguage: form.policyLanguage,
      }),
    onSuccess: () => {
      setForm({
        name: '', tier: '', domain: '', email: '', phone: '', description: '',
        plan: 'TRIAL', planExpiresAt: '', policyLanguage: 'en',
      });
      setError(null);
      setSuggestedRoot(null);
      onSaved();
    },
    onError: (err) => {
      setError(describeError(err));
      // The server says which root it wanted; offer it as a one-click fix rather
      // than making the user work out what was wrong.
      const detail = (err as { details?: { suggestedRoot?: string } }).details;
      setSuggestedRoot(detail?.suggestedRoot ?? null);
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('organizations.add')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" disabled={!form.name.trim() || !tier || create.isPending}
                  onClick={() => create.mutate()}>
            {create.isPending ? t('common.saving') : t('common.create')}
          </Button>
        </>
      }
    >
      {error && (
        <div className="mb-4">
          <ErrorNotice message={error} />
          {suggestedRoot && (
            <button
              onClick={() => { setForm({ ...form, domain: suggestedRoot }); setError(null); setSuggestedRoot(null); }}
              className="mt-2 text-sm text-brand hover:underline"
            >
              {t('organizations.useSuggestedDomain', { domain: suggestedRoot })}
            </button>
          )}
        </div>
      )}

      {/* Stated, not chosen. See the note on this component. */}
      <p className="oc-muted mb-4">{t('organizations.parentNotice', { parent: parentName })}</p>

      <Field label={t('organizations.name')} htmlFor="o-name" required>
        <input id="o-name" className="oc-input" value={form.name} maxLength={255}
               onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </Field>

      <Field label={t('organizations.tier')} htmlFor="o-tier"
             hint={t('organizations.tierHint')} required>
        <select id="o-tier" className="oc-input" value={tier}
                onChange={(e) => setForm({ ...form, tier: e.target.value })}>
          {creatableTiers.map((candidate) => (
            <option key={candidate} value={candidate}>
              {t(`tier.${candidate}`)}
            </option>
          ))}
        </select>
      </Field>

      <Field label={t('organizations.domain')} htmlFor="o-domain"
             hint={t('organizations.domainHint')}>
        <input id="o-domain" className="oc-input font-mono" value={form.domain}
               placeholder="example.com" autoCapitalize="none" autoCorrect="off"
               onChange={(e) => setForm({ ...form, domain: e.target.value })} />
      </Field>

      <Field label={t('organizations.email')} htmlFor="o-email" hint={t('common.optional')}>
        <input id="o-email" type="email" className="oc-input" value={form.email}
               autoCapitalize="none" autoCorrect="off"
               onChange={(e) => setForm({ ...form, email: e.target.value })} />
      </Field>

      <Field label={t('organizations.phone')} htmlFor="o-phone" hint={t('common.optional')}>
        <input id="o-phone" type="tel" className="oc-input" value={form.phone} maxLength={50}
               onChange={(e) => setForm({ ...form, phone: e.target.value })} />
      </Field>

      <Field label={t('organizations.plan')} htmlFor="o-plan">
        <select id="o-plan" className="oc-input" value={form.plan}
                onChange={(e) => setForm({ ...form, plan: e.target.value as Plan })}>
          {PLANS.map((plan) => (
            <option key={plan} value={plan}>{t(`organizations.plans.${plan}`)}</option>
          ))}
        </select>
      </Field>

      <Field label={t('organizations.planExpiresAt')} htmlFor="o-expiry"
             hint={t('organizations.planExpiryHint')}>
        <input id="o-expiry" type="date" className="oc-input" value={form.planExpiresAt}
               onChange={(e) => setForm({ ...form, planExpiresAt: e.target.value })} />
      </Field>

      <Field label={t('organizations.policyLanguage')} htmlFor="o-lang"
             hint={t('organizations.policyLanguageHint')}>
        <select id="o-lang" className="oc-input" value={form.policyLanguage}
                onChange={(e) => setForm({ ...form, policyLanguage: e.target.value as Language })}>
          {LANGUAGES.map((lang) => (
            <option key={lang} value={lang}>{LANGUAGE_NAMES[lang]}</option>
          ))}
        </select>
      </Field>

      <Field label={t('organizations.description')} htmlFor="o-desc" hint={t('common.optional')}>
        <textarea id="o-desc" className="oc-input min-h-[4.5rem] py-2 resize-y"
                  value={form.description} maxLength={2000}
                  onChange={(e) => setForm({ ...form, description: e.target.value })} />
      </Field>
    </Modal>
  );
}

// ---------------------------------------------------------------------------

/**
 * Edit an organisation.
 *
 * Tier is shown but not editable. Moving an organisation between tiers changes
 * what it may do to every organisation beneath it, so it is not a dropdown on a
 * profile form — and the server rejects it outright.
 *
 * Plan and expiry are hidden on your own row, because a licence is only ever set
 * from above; the server refuses a self-licence with its own error code.
 */
function EditOrganizationModal({ org, onClose, onSaved }: {
  org: ChannelOrganization | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const describeError = useApiError();

  const [form, setForm] = useState<{
    name: string; domain: string; email: string; phone: string; description: string;
    plan: string; planExpiresAt: string; policyLanguage: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [suggestedRoot, setSuggestedRoot] = useState<string | null>(null);

  // Seed from the row the moment a different organisation is opened. Keyed on
  // the id so reopening the dialog never shows the previous organisation's
  // values for a heartbeat before the state catches up.
  const [seededFor, setSeededFor] = useState<string | null>(null);
  if (org && seededFor !== org.id) {
    setSeededFor(org.id);
    setForm({
      name: org.name,
      domain: org.domain ?? '',
      email: org.email ?? '',
      phone: org.phone ?? '',
      description: org.description ?? '',
      plan: org.plan,
      // <input type="date"> speaks YYYY-MM-DD only.
      planExpiresAt: org.planExpiresAt ? org.planExpiresAt.slice(0, 10) : '',
      policyLanguage: org.policyLanguage,
    });
    setError(null);
    setSuggestedRoot(null);
  }

  const update = useMutation({
    mutationFn: () =>
      api.patch(`/organizations/${org?.id}`, {
        name: form?.name,
        domain: form?.domain || null,
        email: form?.email || null,
        phone: form?.phone || null,
        description: form?.description || null,
        policyLanguage: form?.policyLanguage,
        // Omitted entirely for your own organisation: sending them would be
        // rejected, and an empty PATCH field is not the same as "unchanged".
        ...(org && !org.isSelf
          ? { plan: form?.plan, planExpiresAt: form?.planExpiresAt || null }
          : {}),
      }),
    onSuccess: () => { setError(null); onSaved(); },
    onError: (err) => {
      setError(describeError(err));
      const detail = (err as { details?: { suggestedRoot?: string } }).details;
      setSuggestedRoot(detail?.suggestedRoot ?? null);
    },
  });

  if (!org || !form) return null;

  return (
    <Modal
      open={!!org}
      onClose={onClose}
      title={org.name}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" disabled={!form.name.trim() || update.isPending}
                  onClick={() => update.mutate()}>
            {update.isPending ? t('common.saving') : t('common.save')}
          </Button>
        </>
      }
    >
      {error && (
        <div className="mb-4">
          <ErrorNotice message={error} />
          {suggestedRoot && (
            <button
              onClick={() => { setForm({ ...form, domain: suggestedRoot }); setError(null); setSuggestedRoot(null); }}
              className="mt-2 text-sm text-brand hover:underline"
            >
              {t('organizations.useSuggestedDomain', { domain: suggestedRoot })}
            </button>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-1.5 mb-4">
        <Pill tone={TIER_TONE[org.tier] ?? 'neutral'}>
          {t(`tier.${org.tier}`, { defaultValue: org.tier })}
        </Pill>
        <span className="oc-pill-neutral">{t('organizations.tierFixed')}</span>
      </div>

      <Field label={t('organizations.name')} htmlFor="e-name" required>
        <input id="e-name" className="oc-input" value={form.name} maxLength={255}
               onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </Field>

      <Field label={t('organizations.domain')} htmlFor="e-domain"
             hint={t('organizations.domainHint')}>
        <input id="e-domain" className="oc-input font-mono" value={form.domain}
               placeholder="example.com" autoCapitalize="none" autoCorrect="off"
               onChange={(e) => setForm({ ...form, domain: e.target.value })} />
      </Field>

      <Field label={t('organizations.email')} htmlFor="e-email" hint={t('common.optional')}>
        <input id="e-email" type="email" className="oc-input" value={form.email}
               autoCapitalize="none" autoCorrect="off"
               onChange={(e) => setForm({ ...form, email: e.target.value })} />
      </Field>

      <Field label={t('organizations.phone')} htmlFor="e-phone" hint={t('common.optional')}>
        <input id="e-phone" type="tel" className="oc-input" value={form.phone} maxLength={50}
               onChange={(e) => setForm({ ...form, phone: e.target.value })} />
      </Field>

      {!org.isSelf && (
        <>
          <Field label={t('organizations.plan')} htmlFor="e-plan">
            <select id="e-plan" className="oc-input" value={form.plan}
                    onChange={(e) => setForm({ ...form, plan: e.target.value })}>
              {PLANS.map((plan) => (
                <option key={plan} value={plan}>{t(`organizations.plans.${plan}`)}</option>
              ))}
            </select>
          </Field>

          <Field label={t('organizations.planExpiresAt')} htmlFor="e-expiry"
                 hint={t('organizations.planExpiryEditHint')}>
            <input id="e-expiry" type="date" className="oc-input" value={form.planExpiresAt}
                   onChange={(e) => setForm({ ...form, planExpiresAt: e.target.value })} />
          </Field>
        </>
      )}

      <Field label={t('organizations.policyLanguage')} htmlFor="e-lang"
             hint={t('organizations.policyLanguageHint')}>
        <select id="e-lang" className="oc-input" value={form.policyLanguage}
                onChange={(e) => setForm({ ...form, policyLanguage: e.target.value })}>
          {LANGUAGES.map((lang) => (
            <option key={lang} value={lang}>{LANGUAGE_NAMES[lang]}</option>
          ))}
        </select>
      </Field>

      <Field label={t('organizations.description')} htmlFor="e-desc" hint={t('common.optional')}>
        <textarea id="e-desc" className="oc-input min-h-[4.5rem] py-2 resize-y"
                  value={form.description} maxLength={2000}
                  onChange={(e) => setForm({ ...form, description: e.target.value })} />
      </Field>
    </Modal>
  );
}

// ---------------------------------------------------------------------------

/**
 * The consent toggle.
 *
 * Withdrawal deletes every external membership, which signs real people out of
 * this organisation. That is a consequence nobody should discover afterwards, so
 * it is spelled out before the button is pressed and the button is destructive
 * red. Granting gets the same treatment in the other direction: the dialog says
 * plainly that it opens the organisation's compliance records — its assessments,
 * gaps and evidence — to the organisation above it.
 *
 * It also states what consent does NOT affect, because the commonest fear here
 * is that withdrawing will break billing or hide the account. It will not:
 * administrative reach is separate and flows down the channel regardless.
 */
function ConsentModal({ org, onClose, onSaved }: {
  org: ChannelOrganization | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const describeError = useApiError();
  const [error, setError] = useState<string | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);

  const withdrawing = org?.managementConsent ?? false;

  const save = useMutation({
    mutationFn: () =>
      api.post<{ removedExternalMembers: number }>(`/organizations/${org?.id}/consent`, {
        granted: !withdrawing,
      }),
    onSuccess: () => { setAcknowledged(false); setError(null); onSaved(); },
    onError: (err) => setError(describeError(err)),
  });

  if (!org) return null;

  return (
    <Modal
      open={!!org}
      onClose={() => { setAcknowledged(false); onClose(); }}
      title={withdrawing ? t('organizations.withdrawConsent') : t('organizations.grantConsent')}
      footer={
        <>
          <Button onClick={() => { setAcknowledged(false); onClose(); }}>
            {t('common.cancel')}
          </Button>
          <Button
            variant={withdrawing ? 'danger' : 'primary'}
            disabled={!acknowledged || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? t('common.saving') : t('common.confirm')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <ErrorNotice message={error} />}

        <p className="font-medium text-ink">{org.name}</p>

        <div className={cn(
          'rounded-xl px-3 py-2.5 flex items-start gap-2',
          withdrawing ? 'bg-critical/[0.08]' : 'bg-surface-sunken',
        )}>
          <IconShield className={cn('w-4 h-4 shrink-0 mt-0.5',
            withdrawing ? 'text-critical' : 'text-brand')} />
          <p className="text-xs text-ink-secondary leading-relaxed">
            {withdrawing
              ? t('organizations.withdrawWarning')
              : t('organizations.grantWarning')}
          </p>
        </div>

        {/* The reassurance is as load-bearing as the warning: without it, people
            refuse consent for fear of losing an account that was never at risk. */}
        <p className="text-xs text-ink-muted leading-relaxed">
          {t('organizations.consentScopeNote')}
        </p>

        {withdrawing && org.userCount > 0 && (
          <p className="text-xs text-ink-secondary">
            {t('organizations.withdrawMemberNote', { count: org.userCount })}
          </p>
        )}

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5 w-4 h-4 accent-[rgb(var(--brand))]"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
          />
          <span className="text-sm text-ink">
            {withdrawing
              ? t('organizations.withdrawAcknowledge')
              : t('organizations.grantAcknowledge')}
          </span>
        </label>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------

/**
 * Deactivate or reactivate.
 *
 * The dialog says explicitly that nothing is deleted, because "deactivate" in
 * most software means "delete, eventually". Here it does not: assessments, gaps,
 * the credit ledger and the audit trail all survive, and the account can be
 * reopened from this same screen.
 */
function DeactivateModal({ org, onClose, onSaved }: {
  org: ChannelOrganization | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const describeError = useApiError();
  const [error, setError] = useState<string | null>(null);

  const deactivating = org?.isActive ?? false;

  const save = useMutation({
    mutationFn: () =>
      deactivating
        ? api.delete(`/organizations/${org?.id}`)
        : api.post(`/organizations/${org?.id}/reactivate`),
    onSuccess: () => { setError(null); onSaved(); },
    onError: (err) => setError(describeError(err)),
  });

  if (!org) return null;

  return (
    <Modal
      open={!!org}
      onClose={onClose}
      title={deactivating ? t('organizations.deactivate') : t('organizations.reactivate')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant={deactivating ? 'danger' : 'primary'} disabled={save.isPending}
                  onClick={() => save.mutate()}>
            {save.isPending ? t('common.saving') : t('common.confirm')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <ErrorNotice message={error} />}
        <p className="font-medium text-ink">{org.name}</p>
        <p className="text-sm text-ink-secondary leading-relaxed">
          {deactivating
            ? t('organizations.deactivateWarning')
            : t('organizations.reactivateNotice')}
        </p>
        {deactivating && (
          <p className="text-xs text-ink-muted leading-relaxed">
            {t('organizations.deactivateRetentionNote')}
          </p>
        )}
      </div>
    </Modal>
  );
}
